import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Sparkles, Zap } from "lucide-react";
import { Shell } from "@/components/Shell";
import { QuestionField } from "@/components/QuestionField";
import { getSections, visibleQuestions } from "@/data/questionnaire";
import { computeStrategy, AREAS } from "@/data/strategy";
import { getSubmission, upsertSubmission, loadWeights } from "@/lib/storage";
import { contributeAllQuiet } from "@/data/benchmark";

const TIERS = [
  { key: "primary", title: "Primarie", color: "#C85A32" },
  { key: "secondary", title: "Secondarie", color: "#B07A12" },
  { key: "tertiary", title: "Terziarie", color: "#6E8358" },
];

const LivePanel = ({ strategy, answered, total }) => (
  <div className="card p-5 sm:p-6" data-testid="live-panel">
    <div className="flex items-center gap-2">
      <Zap className="text-[#D99B26]" size={18} />
      <p className="eyebrow">Risultato in tempo reale</p>
    </div>
    <div className="flex justify-between text-xs text-[#7A6B63] mt-3 mb-1.5">
      <span data-testid="live-answered">{answered} risposte su {total}</span>
      <span>{Math.round((answered / total) * 100)}%</span>
    </div>
    <div className="h-2 rounded-full bg-[#EAE0D0] overflow-hidden">
      <div className="h-full bg-gradient-to-r from-[#C85A32] to-[#D99B26] transition-all duration-500" style={{ width: `${(answered / total) * 100}%` }} />
    </div>
    <p className="font-serif text-2xl font-semibold text-[#2D4030] mt-5 leading-tight" data-testid="live-strategy">{strategy.label}</p>
    {strategy.pace && <p className="text-xs text-[#7A6B63] mt-1">{strategy.pace}</p>}
    <div className="grid gap-4 mt-5">
      {TIERS.map((t) => (
        <div key={t.key} data-testid={`live-${t.key}`}>
          <p className="text-xs uppercase tracking-widest font-semibold" style={{ color: t.color }}>{t.title}</p>
          {strategy[t.key].length === 0 ? (
            <p className="text-xs italic text-[#B0A096] mt-1">—</p>
          ) : (
            <ol className="mt-1.5 grid gap-1.5">
              {strategy[t.key].map((a, i) => (
                <li key={a.id} className="flex gap-2 text-sm leading-snug text-[#2C221E]">
                  <span className="flex-none font-serif font-semibold" style={{ color: t.color }}>{i + 1}</span>
                  <span>{a.title} <span className="text-[#8A7970] text-xs">· {AREAS[a.area]}</span></span>
                </li>
              ))}
            </ol>
          )}
        </div>
      ))}
    </div>
  </div>
);

export default function LivePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sub, setSub] = useState(() => getSubmission(id));
  const weights = useMemo(loadWeights, []);

  useEffect(() => {
    if (!sub) navigate("/", { replace: true });
  }, [sub, navigate]);
  if (!sub) return null;

  const sections = getSections(sub.businessType);
  const strategy = computeStrategy(sub.answers, weights, sub.businessType);
  const visible = sections.flatMap((s) => visibleQuestions(s, sub.answers));
  const answered = visible.filter((q) => {
    const v = sub.answers[q.id];
    return v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0);
  }).length;

  const setAnswer = (qid, v) => setSub(upsertSubmission({ ...sub, answers: { ...sub.answers, [qid]: v } }));
  const setName = (respondent) => setSub(upsertSubmission({ ...sub, respondent }));
  const finish = () => {
    upsertSubmission({ ...sub, status: "completed", completedAt: new Date().toISOString(), report: "", reportDone: false });
    contributeAllQuiet();
    navigate(`/risultato/${id}`);
  };

  let n = 0;
  return (
    <Shell wide header={<button className="btn btn-ghost !min-h-[44px] text-sm" onClick={() => navigate("/")} data-testid="live-back-btn"><ArrowLeft size={16} /> Elenco</button>}>
      <div className="fade-up">
        <p className="eyebrow">Compilazione rapida</p>
        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#2C221E] mt-2">Tutto in una pagina, risultato subito</h1>
        <label className="block mt-6 max-w-md">
          <span className="eyebrow">Chi risponde</span>
          <input className="field mt-2" value={sub.respondent} onChange={(e) => setName(e.target.value)} placeholder="es. Maria e Giovanni" data-testid="live-respondent-input" />
        </label>
      </div>

      <div className="grid lg:grid-cols-[1fr_340px] gap-8 mt-8 items-start">
        <div className="grid gap-8 order-2 lg:order-1">
          {sections.map((s, si) => (
            <section key={s.id} className="card p-6 sm:p-8" data-testid={`live-section-${s.id}`}>
              <p className="eyebrow">Sezione {si + 1}</p>
              <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#2D4030] mt-1 mb-6">{s.title}</h2>
              <div className="grid gap-8">
                {visibleQuestions(s, sub.answers).map((q) => {
                  n += 1;
                  return (
                    <div key={q.id} data-testid={`live-question-${q.id}`}>
                      <p className="text-base sm:text-lg font-semibold text-[#2C221E] leading-snug mb-3">
                        <span className="font-serif text-[#C85A32] mr-2">{n}.</span>{q.text}
                        {q.hint && <span className="text-sm font-normal text-[#7A6B63]"> · {q.hint}</span>}
                      </p>
                      <QuestionField q={q} value={sub.answers[q.id]} onChange={(v) => setAnswer(q.id, v)} />
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
          <button className="btn btn-primary text-lg !min-h-[64px] !px-9 justify-self-end" onClick={finish} data-testid="live-finish-btn">
            <Sparkles size={20} /> Chiudi e genera il report AI
          </button>
        </div>
        <div className="order-1 lg:order-2 lg:sticky lg:top-6">
          <LivePanel strategy={strategy} answered={answered} total={visible.length} />
        </div>
      </div>
    </Shell>
  );
}
